import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Chat.css';

const Chat = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [chatPartners, setChatPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [friendRequestInput, setFriendRequestInput] = useState('');
  const wsRef = useRef(null);
  const navigate = useNavigate();
  const messagesRef = useRef(null);
  const processedMessagesRef = useRef(new Set());


   // Define static agents as "friends"
   const agents = [
    { username: "EntertainmentAgent", displayName: "Entertainment Agent" },
    { username: "SelfImprovementAgent", displayName: "Self-Improvement Agent" },
    { username: "PsychologistAgent", displayName: "Personal Psychologist Agent" }
  ];

  const fetchChatPartners = async () => {
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/partners`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch chat partners');
        const partners = await response.json();
        console.log('Chat partners:', partners); // Debug
        setChatPartners(partners);
    } catch (error) {
        console.error('Error fetching chat partners:', error);
    }
  };

  const fetchChatHistory = async (senderUsername, receiverUsername) => {
    console.log('Fetching chat history:', { senderUsername, receiverUsername });
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/history`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ sender_username: senderUsername, receiver_username: receiverUsername }),
        });
        if (!response.ok) throw new Error('Failed to fetch chat history');
        const history = await response.json();
        console.log('Chat history:', history);
        setMessages(history);
    } catch (error) {
        console.error('Error fetching chat history:', error);
    }
  };

  const fetchFriendRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/friends/requests`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch friend requests');
      const requests = await response.json();
      setFriendRequests(requests);
    } catch (error) {
      console.error('Error fetching friend requests:', error);
    }
  };

  const sendFriendRequest = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/friends/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ receiver_username: friendRequestInput }),
      });
      if (!response.ok) throw new Error('Failed to send friend request');
      const result = await response.json();
      console.log(result.message);
      setFriendRequestInput('');
    } catch (error) {
      console.error('Error sending friend request:', error);
    }
  };

  const respondFriendRequest = async (requestId, accept) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/friends/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ request_id: requestId, accept }),
      });
      if (!response.ok) throw new Error('Failed to respond to friend request');
      const result = await response.json();
      console.log(result.message);
      fetchFriendRequests();
      if (accept) fetchChatPartners();
    } catch (error) {
      console.error('Error responding to friend request:', error);
    }
  };

  const sendGrokRequest = async (prompt) => {
    console.log('sendGrokRequest called:', { selectedPartner, prompt });
    if (!selectedPartner || !selectedPartner.is_agent) {
        console.log('Exiting early:', { selectedPartner });
        return;
    }
    let agentType = selectedPartner.username.split('_')[0].replace(/Agent$/i, '').toLowerCase();
    if (agentType === 'selfimprovement') {
        agentType = 'self_improvement';
    }
    const userPrompt = prompt || "Tell me something";
    try {
        setMessages((prev) => [...prev, {
            sender_username: user.username,
            message: userPrompt,
            timestamp: new Date().toISOString()
        }]);
        
        const token = localStorage.getItem('token');
        const payload = { 
            receiver_username: selectedPartner.username, 
            prompt: userPrompt,
            agent_type: agentType 
        };
        console.log('Sending payload:', payload);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/grok/request`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            let errorMessage = 'Sorry, I couldn\'t process your request. Please try again later.';
            try {
                const errorData = await response.json();
                console.log('Error response:', errorData);
                errorMessage = errorData.detail?.message || errorMessage;
            } catch (jsonError) {
                console.error('Failed to parse error response:', jsonError);
            }
            setMessages((prev) => [...prev, {
                sender_username: selectedPartner.username,
                message: errorMessage,
                timestamp: new Date().toISOString()
            }]);
            throw new Error(`Grok request failed: ${errorMessage}`);
        }
        const result = await response.json();
        console.log(`${agentType} Agent response:`, result.message);
        setMessages((prev) => [...prev, {
            sender_username: selectedPartner.username,
            message: result.message,
            timestamp: new Date().toISOString()
        }]);
        if (result.message.startsWith('Your MBTI type is')) {
            setMessages((prev) => [...prev, {
                sender_username: selectedPartner.username,
                message: "I've sent friend requests to users with compatible personality types!",
                timestamp: new Date().toISOString()
            }]);
        }
    } catch (error) {
        console.error(`Error sending ${selectedPartner.displayName} request:`, error);
    }
  };

  useEffect(() => {
    if (!user || !user.username) {
      navigate('/');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const wsUrl = `${process.env.REACT_APP_WS_URL}/ws/chat?token=${token}`;
    const websocket = new WebSocket(wsUrl);
    wsRef.current = websocket;

    websocket.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      fetchChatPartners();
      fetchFriendRequests();
    };
    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Received WebSocket message:', data);

      if (data.type === 'friend_request') {
        console.log(`Friend request from ${data.sender_username}`);
        fetchFriendRequests();
        return;
      }
      if (data.type === 'friend_response') {
        console.log(`Friend request response from ${data.receiver_username}: ${data.accepted ? 'accepted' : 'rejected'}`);
        if (data.accepted) fetchChatPartners();
        return;
      }
      if (data.type === 'grok_response') {
        setMessages((prev) => [...prev, {
          sender_username: data.sender_username,
          message: data.message,
          timestamp: data.timestamp
        }]);
        return;
      }
    
      const msg = data;
      const messageId = `${msg.sender_username}-${msg.timestamp}`;
      if (processedMessagesRef.current.has(messageId)) return;
      processedMessagesRef.current.add(messageId);

      const sender = chatPartners.find((p) => p.username === msg.sender_username);
      if (!sender) {
        setPendingMessages((prev) => {
          if (!prev.some((m) => m.timestamp === msg.timestamp && m.sender_username === msg.sender_username)) {
            return [...prev, msg];
          }
          return prev;
        });
        fetchChatPartners();
        return;
      }

      if (selectedPartner && msg.sender_username === selectedPartner.username) {
        setMessages((prev) => [...prev, msg]);
      } else if (!selectedPartner) {
        setSelectedPartner(sender);
        setMessages((prev) => [...prev, msg]);
      } else {
        setPendingMessages((prev) => {
          if (!prev.some((m) => m.timestamp === msg.timestamp && m.sender_username === msg.sender_username)) {
            return [...prev, msg];
          }
          return prev;
        });
      }
    };
    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
      setIsConnected(false);
    };
    websocket.onclose = () => {
      console.log('WebSocket closed');
      setIsConnected(false);
    };

    return () => websocket.close();
  }, [user, navigate]);

  useEffect(() => {
    if (isConnected) {
      fetchChatPartners();
      fetchFriendRequests();
    }
  }, [isConnected]);

  useEffect(() => {
    if (pendingMessages.length === 0) return;

    const newMessages = [];
    const remainingPending = [];

    pendingMessages.forEach((msg) => {
      const sender = chatPartners.find((p) => p.username === msg.sender_username);
      if (!sender) {
        remainingPending.push(msg);
      } else if (selectedPartner && msg.sender_username === selectedPartner.username) {
        newMessages.push(msg);
      } else if (!selectedPartner) {
        setSelectedPartner(sender);
        newMessages.push(msg);
      } else {
        remainingPending.push(msg);
      }
    });

    if (newMessages.length > 0) setMessages((prev) => [...prev, ...newMessages]);
    setPendingMessages(remainingPending);
  }, [chatPartners, selectedPartner]);

  useEffect(() => {
    if (selectedPartner && isConnected) {
        console.log('Triggering chat history for:', selectedPartner.username); // Debug
        fetchChatHistory(user.username, selectedPartner.username);
        setPendingMessages((prev) => prev.filter((msg) => msg.sender_username !== selectedPartner.username));
    }
  }, [selectedPartner, isConnected, user]);

  useEffect(() => {
    if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = () => {
    console.log('sendMessage called:', { selectedPartner, message });
    const trimmedMessage = message.trim();
    if (wsRef.current && isConnected && trimmedMessage && selectedPartner) {
        console.log('is_agent:', selectedPartner.is_agent); // Debug
        if (selectedPartner.is_agent) {
            console.log('Sending to agent');
            sendGrokRequest(trimmedMessage);
        } else {
            console.log('Sending to WebSocket');
            const payload = { receiver_username: selectedPartner.username, message: trimmedMessage };
            const localMsg = { sender_username: user.username, message: trimmedMessage, timestamp: new Date().toISOString() };
            wsRef.current.send(JSON.stringify(payload));
            setMessages((prev) => [...prev, localMsg]);
        }
        setMessage('');
    } else {
        console.error('Cannot send message: Missing data or not connected');
    }
};

  const getQueuedMessageCount = (partnerUsername) => {
    return pendingMessages.filter((msg) => msg.sender_username === partnerUsername).length;
  };

  if (!user || !user.username) return null;

  return (
    <div className="chat-container">
      <div className="sidebar">
        <h3>Chats</h3>

        <h4>Agents</h4>
        <ul className="agents-list">
            {chatPartners.filter(p => p.is_agent).map((partner) => {
                const queuedCount = getQueuedMessageCount(partner.username);
                const displayName = partner.username.split('_')[0].replace(/Agent$/i, '').replace('SelfImprovement', 'Self Improvement') + " Agent";
                return (
                    <li
                        key={partner.username}
                        className={selectedPartner?.username === partner.username ? 'selected' : ''}
                        onClick={() => {
                            console.log('Selected partner:', partner);
                            setSelectedPartner({ ...partner, displayName });
                        }}
                    >
                        {displayName}
                        {queuedCount > 0 && <span className="queued-count"> {queuedCount}</span>}
                    </li>
                );
            })}
        </ul>
        <div className="friend-request-input">
          <input
            type="text"
            value={friendRequestInput}
            onChange={(e) => setFriendRequestInput(e.target.value)}
            placeholder="Send friend request"
          />
          <button onClick={sendFriendRequest}>Send</button>
        </div>
        <h4>Pending Requests</h4>
        <ul>
          {friendRequests.map((req) => (
            <li key={req.id}>
              {req.sender_username}
              <button onClick={() => respondFriendRequest(req.id, true)}>Accept</button>
              <button onClick={() => respondFriendRequest(req.id, false)}>Reject</button>
            </li>
          ))}
        </ul>

        <h4>Friends</h4>
        <ul>
            {chatPartners.filter(p => p.is_agent === false).map((partner) => {
                const queuedCount = getQueuedMessageCount(partner.username);
                return (
                    <li
                        key={partner.username}
                        className={selectedPartner?.username === partner.username ? 'selected' : ''}
                        onClick={() => setSelectedPartner(partner)}
                    >
                        {partner.username}
                        {queuedCount > 0 && <span className="queued-count"> {queuedCount}</span>}
                    </li>
                );
            })}
        </ul>
      </div>


      <div className="chat-main">
        <h3>
            {user.username} {isConnected ? '(On)' : '(Off)'}
            {selectedPartner && ` - Chatting with ${selectedPartner.displayName || selectedPartner.username}`}
        </h3>
        <div className="messages" ref={messagesRef}>
            {selectedPartner ? (
                messages.map((msg, idx) => {
                    const messageClass = msg.sender_username === user.username ? 'own-message' : 'other-message';
                    // console.log('Rendering message:', {
                    //     idx,
                    //     message: msg.message,
                    //     sender_username: msg.sender_username,
                    //     isAgentChat: selectedPartner.is_agent,
                    //     messageClass
                    // });
                    return (
                        <div
                            key={idx}
                            className={`message-box ${messageClass}`}
                        >
                            <p className="message-text">{msg.message}</p>
                            <p className="message-date">{new Date(msg.timestamp).toLocaleString()}</p>
                        </div>
                    );
                })
            ) : (
                <p>Select a friend or agent to start messaging</p>
            )}
        </div>
        {selectedPartner && (
          <div className="input-container">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder={`Message ${selectedPartner.displayName || selectedPartner.username}`}
            />
            <div className="send-icon" onClick={sendMessage}>
              <span>➡️</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;